import React from 'react'
import { RegionalWALogo } from './components/RegionalWALogo'
import {
    StyledLogoContainer,
    StyledRegionalItem,
    StyledRegionList,
    StyledRegionalLogo,
    StyledWebLink,
    StyledWeAreForRegionalContainer,
    StyledLogoNavLink,
} from './our-publication.styled'
import { Headline } from '../publication/Headline/Headline'
import { useSelector } from 'react-redux'
import { AppState } from '@news-mono/web-common'
import { MetaState } from '@news-mono/web-common'
import { TheWestSectionMetaDTO } from '@west-australian-newspapers/publication-types'
import { WeAreForRegional } from './components/WeAreForRegional'
import { TheWestSection } from '../__styling/settings/sections/thewest'

const albanyExtraLogo = require('./components/the-extra-publisher-logo-60px-high.png')

export interface PublicationListItemProps {
    title: string | undefined
    domain: string | undefined
    logo: any
    contact: any
}

export const PublicationListItem: React.FC<PublicationListItemProps> = (
    props,
) => {
    const { title, domain, logo, contact } = props

    return (
        <StyledRegionalItem>
            <RegionalLogoContainer domain={domain}>
                <StyledRegionalLogo
                    src={logo.image || logo}
                    alt={`${title} Logo`}
                />
            </RegionalLogoContainer>
            <div>
                {contact.email && (
                    <StyledWebLink
                        aria-label={`Email ${title}`}
                        to={`mailto:${contact.email}`}
                    >
                        Email us
                    </StyledWebLink>
                )}
                {contact.phone && (
                    <StyledWebLink
                        aria-label={`Call ${title}`}
                        to={`tel:${contact.phone}`}
                    >
                        Call us
                    </StyledWebLink>
                )}
            </div>
        </StyledRegionalItem>
    )
}

export const RegionalLogoContainer: React.FC<{
    children: React.ReactElement
    domain: string | undefined
}> = ({ children, domain }) =>
    domain ? (
        <StyledLogoNavLink
            to={`https://${domain}`}
            openExternalInNewWindow={true}
            rel="noopener"
        >
            {children}
        </StyledLogoNavLink>
    ) : (
        children
    )

export const OurPublications: React.FC = () => {
    const { sectionMeta } = useSelector<AppState, MetaState>(
        (state) => state.meta,
    )
    const sectionOverrides = sectionMeta.sectionOverrides as TheWestSectionMetaDTO
    const sectionOverrideKeys = Object.keys(sectionOverrides) as Array<
        Exclude<TheWestSection, 'default'>
    >

    return (
        <React.Fragment>
            <StyledLogoContainer>
                <RegionalWALogo width={274} height={96} />
            </StyledLogoContainer>
            <Headline text="Exclusive news from all of our Western Australian publications" />
            <StyledRegionList>
                {sectionOverrideKeys.map((key) => {
                    if (typeof key !== 'string') {
                        return null
                    }

                    const section = sectionOverrides[key]

                    if (!section) {
                        return undefined
                    }

                    const title = section.Title
                    const logo = section.PublisherLogo
                    const contact = section.contact
                    const domain = section.Hostname

                    if (!title || !logo || !contact) {
                        return undefined
                    }

                    /**
                     * DPO-669:
                     * The Albany Advertiser publishes a free paper called The Extra every Friday, sales reps
                     * request that we include a logo for it alongside our other regional publications.
                     */
                    if (key === 'albany-advertiser') {
                        return (
                            <React.Fragment key="fragment">
                                <PublicationListItem
                                    key={key}
                                    title={title}
                                    domain={domain}
                                    contact={contact}
                                    logo={logo}
                                />

                                <PublicationListItem
                                    key="albany-extra"
                                    title="The Extra"
                                    domain={domain}
                                    contact={contact}
                                    logo={albanyExtraLogo}
                                />
                            </React.Fragment>
                        )
                    }

                    return (
                        <PublicationListItem
                            key={key}
                            title={title}
                            domain={domain}
                            contact={contact}
                            logo={logo}
                        />
                    )
                })}
            </StyledRegionList>
            <StyledWeAreForRegionalContainer>
                <WeAreForRegional />
            </StyledWeAreForRegionalContainer>
        </React.Fragment>
    )
}

export default OurPublications
